import React, { useContext, useEffect, useState } from "react";
import SearchBar from "~/assets/js/components/KonfiguratorSearchBar";
import i18next from "i18next";
import HorizontalAddonCard from "~/assets/js/components/AddonCard/HorizontalAddonCard";
import { ProductsDetailsContext } from "~/assets/js/pages/ProductDetails/ProductsProvider";
import { ForwardButton } from "~/assets/js/components/Button";
import PropTypes from "prop-types";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import { formatPrice, selectedIngredientsQuantity } from "~/assets/js/Format";
import { ACTIONS, ITEM_TYPE, UPDATE_TYPE } from "../../ProductsProvider";
import { useIngredientsStock } from "~/assets/js/Api/ingredientsApi";
import OverlayLoader from "~/assets/js/components/Loaders/OverlayLoader";
import useThereIsMoreToScroll from "~/assets/js/Helpers/hooks/useThereIsMoreToScroll";
import ThereIsMore from "../../components/ThereIsMore";
import { DeliveryContext } from "~/assets/js/models/DeliveryContext";
import { calculateFranchisePrice } from "~/assets/js/Helpers/util";
import TagsFilter from "~/assets/js/components/TagFilters";
import ProductModifiedNotification from "../../components/ProductModifiedNotification";

const TAGS_FILTERS = {
    vegan: {
        hintText: "categoryItems.vegan",
        icon: "vegan",
        filter: "vegan",
    },
    vegetarisch: {
        hintText: "categoryItems.vegetarian",
        icon: "vegetarian",
        filter: "vegetarisch",
    },
    glutenFree: {
        hintText: "categoryItems.glutenFree",
        icon: "gluten-free",
        filter: "glutenfrei",
    },
    sugarFree: {
        hintText: "categoryItems.sugarFree",
        icon: "sugar-free",
        filter: "zuckerfrei",
    },
    lowCarbs: {
        hintText: "categoryItems.lowCarbs",
        icon: "low-carb",
        filter: "weniger-als-30-kalorien",
    },
};

const ExtraIngredients = ({ handleClose, onClickForward, discounted }) => {
    const product = pottsalatProduct;
    const { storeId } = useContext(DeliveryContext);
    const { extras, isLoading } = useIngredientsStock(storeId);
    const { state, fullPrice, greenClubMembershipPrice, isSoup } = useContext(
        ProductsDetailsContext
    );
    const filteredExtras = isSoup
        ? extras.filter((ingredient) => {
              if (ingredient.attributes.includes("ramen")) return ingredient;
          })
        : extras;

    const [filteredIngredients, setFilteredIngredients] = useState([]);
    const [useSearchFilter, setUseSearchFilter] = useState(false);
    const [searchBarFocused, setSearchBarFocused] = useState(false);

    const { isDesktop } = useViewport();

    useEffect(() => {
        setFilteredIngredients(filteredExtras);
    }, []);

    const categoriesFilter = (category) => {
        let filteredIngredients = [];

        filteredExtras.map((addon) => {
            if (addon.tags.includes(category)) {
                filteredIngredients.push(addon);
            }
        });
        setFilteredIngredients(filteredIngredients);
    };

    return (
        <div id="extra-addons">
            <div className="extra-addons__header">
                <img src={product.images[0].src} alt={product.title} />
                <div className="title">
                    <p className="title__small">
                        {i18next.t("extraIngredients.ingredientsFor")}
                    </p>
                    <p className="title__big">{product.title}</p>
                </div>
                <p className="quantity">
                    {i18next.t("extraIngredients.selected", {
                        count: selectedIngredientsQuantity(state.extras),
                    })}
                </p>
                {isDesktop && (
                    <ForwardButton
                        onClick={onClickForward}
                        size="SM"
                        label={i18next.t("cart.continue")}
                        customStyle="extra-addons__header__button"
                    />
                )}
            </div>
            <div className="extra-addons__content">
                <SearchBar
                    placeholder={i18next.t(
                        "extraIngredients.searchPlaceholder"
                    )}
                    data={filteredExtras}
                    searchKeys={["name"]}
                    autoCompleteKey="name"
                    ingredients={filteredExtras}
                    onClear={() => {
                        setFilteredIngredients(filteredExtras);
                        setUseSearchFilter(false);
                    }}
                    onFocus={() => setSearchBarFocused(true)}
                    onBlur={() => setSearchBarFocused(false)}
                    onSearch={(results) => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        if (results.length > 0) {
                            setFilteredIngredients(
                                results.map((item) => item.item)
                            );
                            setUseSearchFilter(true);
                        } else {
                            setFilteredIngredients(filteredExtras);
                            setUseSearchFilter(false);
                        }
                    }}
                />
                <TagsFilter
                    tags={TAGS_FILTERS}
                    filter={categoriesFilter}
                    clear={() => {
                        setFilteredIngredients(filteredExtras);
                    }}
                />
                {isLoading ? (
                    <OverlayLoader />
                ) : (
                    <>
                        {searchBarFocused && !useSearchFilter ? (
                            <div
                                style={{
                                    height: "60vh",
                                    flex: 1,
                                }}
                            />
                        ) : null}
                        <ExtraIngredientsContainer
                            style={
                                searchBarFocused && !useSearchFilter
                                    ? {
                                          height: 0,
                                          overflow: "hidden",
                                          opacity: 0,
                                      }
                                    : {}
                            }
                            addons={
                                filteredIngredients
                                    ? filteredIngredients
                                    : filteredExtras
                            }
                        />
                    </>
                )}
            </div>
            <div className="extra-addons__footer">
                <div className="close" onClick={handleClose}>
                    <p>{i18next.t("extraIngredients.close")}</p>
                </div>
                <div className="right-side">
                    <div className="price-container">
                        <p className="price">
                            {discounted ? greenClubMembershipPrice : fullPrice}
                        </p>
                        <p className="tax">
                            {i18next.t("productInfo.taxRate", {
                                rate: product.taxRate,
                            })}
                        </p>
                    </div>
                    <ForwardButton
                        onClick={onClickForward}
                        size="SM"
                        label={i18next.t("cart.continue")}
                    />
                </div>
            </div>
        </div>
    );
};

const ExtraIngredientsContainer = ({ addons, ...props }) => {
    const { state, updateProductState, isModified } = useContext(
        ProductsDetailsContext
    );
    const { storeId } = useContext(DeliveryContext);
    const addIngredient = (addon) => {
        const data = { ...addon, quantity: 1 };
        return updateProductState({ type: ACTIONS.ADD_EXTRA, payload: data });
    };

    const increment = (product_id, itemType) => {
        const data = { product_id, update: UPDATE_TYPE.INCREMENT, itemType };
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const decrement = (product_id, quantity, itemType) => {
        const data = { product_id, itemType };

        if (quantity === 1) {
            return updateProductState({ type: ACTIONS.REMOVE, payload: data });
        }

        data.update = UPDATE_TYPE.DECREMENT;
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const { thereIsMore, scrollRef, handleScroll } = useThereIsMoreToScroll(
        100,
        [addons]
    );

    return (
        <div
            className="addons-wrapper"
            ref={scrollRef}
            onScroll={handleScroll}
            {...props}>
            {isModified && <ProductModifiedNotification />}
            <p className="addons-wrapper__title">
                {i18next.t("extraIngredients.title", {
                    quantity: `(${addons.length})`,
                })}
            </p>
            {addons
                .sort((a, b) => (a.name > b.name ? -1 : 1))
                .sort((a, b) =>
                    a.sorting_priority > b.sorting_priority ? -1 : 1
                )
                .map((addon) => {
                    let addonProps = {};

                    const extraIngredient = state.extras.find(
                        (extra) => extra.product_id === addon.product_id
                    );

                    if (extraIngredient) {
                        addonProps = {
                            name: extraIngredient.name,
                            image: extraIngredient.images.normal,
                            details: extraIngredient.details,
                            price: formatPrice(
                                calculateFranchisePrice(
                                    storeId,
                                    extraIngredient
                                ) * extraIngredient.quantity
                            ),
                            quantity: extraIngredient.quantity,
                            increment: () =>
                                increment(addon.product_id, ITEM_TYPE.EXTRA),
                            decrement: () =>
                                decrement(
                                    addon.product_id,
                                    extraIngredient.quantity,
                                    ITEM_TYPE.EXTRA
                                ),
                        };
                    } else {
                        addonProps = {
                            name: addon.name,
                            image: addon.images.normal,
                            details: addon.details,
                            price: formatPrice(
                                calculateFranchisePrice(storeId, addon)
                            ),
                            quantity: 0,
                            increment: () => addIngredient(addon),
                            decrement: () => {}, // Do nothing
                        };
                    }

                    return (
                        <HorizontalAddonCard
                            {...addonProps}
                            key={addon.product_id}
                        />
                    );
                })}
            {thereIsMore && <ThereIsMore />}
        </div>
    );
};

ExtraIngredients.propTypes = {
    discounted: PropTypes.bool,
    handleClose: PropTypes.func,
    onClickForward: PropTypes.func,
};

ExtraIngredientsContainer.propTypes = {
    addons: PropTypes.array,
};

export default ExtraIngredients;
